import React, { useEffect, useMemo, useState } from "react";
import { useRouter } from "expo-router";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useAtomValue, useSetAtom } from "jotai/react";
import { useForm, useFormContext, useWatch } from "react-hook-form";
import { toast } from "sonner-native";

import { healthInsuranceOrCoverageStatusEnumSchema } from "@libs/api/patients";
import { ApplicationScreenLayout } from "@/blocks/ApplicationScreenLayout";
import { ApplicationYesNoButton } from "@/blocks/ApplicationYesNoButton";
import {
  VnAccordion,
  VnAccordionContent,
  VnAccordionItem,
  VnAccordionTrigger,
} from "@/components/VnAccordion";
import { VnBox } from "@/components/VnBox";
import { VnCard, VnCardContent, VnCardTitle } from "@/components/VnCard";
import { BaseCheckboxInput } from "@/components/VnCheckboxInput/BaseCheckboxInput";
import { VnCheckboxInput } from "@/components/VnCheckboxInput/VnCheckboxInput";
import { VnForm } from "@/components/VnForm";
import { VnRadioGroupInput } from "@/components/VnRadioGroupInput/VnRadioGroupInput";
import { VnText } from "@/components/VnText/VnText";
import { VnTextInput } from "@/components/VnTextInput/VnTextInput";
import { useUpdateHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations";
import { useUpdateCurrentPatientMutation } from "@/data/mutations/patientsMutations";
import { useListHouseholdMembersQuery } from "@/data/queries/householdMembersQueries";
import {
  healthInsuranceOrCoverageStatusFormSchema,
  HealthInsuranceOrCoverageStatusFormType,
} from "@/domain/applications";
import { HouseholdMemberType } from "@/domain/householdMember";
import { useTranslator } from "@/hooks/useTranslate";
import {
  applicationCurrentStepAtom,
  applicationTotalStepsAtom,
} from "@/stores/atoms";
import {
  deriveCoverageStatusAnswer,
  getHealthInsuranceOrCoverageStatusOptions,
  getMedicarePlanTypeOptions,
} from "@/utils/applications";
import {
  formatHouseholdMemberName,
  getAllHouseholdMembersNames,
} from "@/utils/householdMember";

type MemberCoverageStatusFormPropsType = {
  member: HouseholdMemberType;
};

const MemberCoverageStatusForm: React.FC<MemberCoverageStatusFormPropsType> = ({
  member,
}) => {
  const form = useFormContext<HealthInsuranceOrCoverageStatusFormType>();

  const coverageOptions = getHealthInsuranceOrCoverageStatusOptions(false);
  const medicarePlanTypeOptions = getMedicarePlanTypeOptions();

  const coverageStatus = useWatch({
    control: form.control,
    name: `${member.id}.healthInsuranceOrCoverageStatus`,
  });

  // Remove the not-covered option when user selects any other coverage option.
  useEffect(() => {
    if (!coverageStatus || coverageStatus.length <= 1) return;

    const hasNotCovered = coverageStatus.includes(
      healthInsuranceOrCoverageStatusEnumSchema.enum.NOT_COVERED,
    );
    const hasOtherOptions = coverageStatus.some(
      status =>
        status !== healthInsuranceOrCoverageStatusEnumSchema.enum.NOT_COVERED,
    );

    if (hasNotCovered && hasOtherOptions) {
      const filteredStatus = coverageStatus.filter(
        status =>
          status !== healthInsuranceOrCoverageStatusEnumSchema.enum.NOT_COVERED,
      );
      form.setValue(
        `${member.id}.healthInsuranceOrCoverageStatus`,
        filteredStatus,
      );
    }
  }, [coverageStatus, form, member.id]);

  const shouldShowOtherField = coverageStatus?.includes(
    healthInsuranceOrCoverageStatusEnumSchema.enum.OTHER,
  );
  const isSectionSelected =
    coverageStatus &&
    coverageStatus.length > 0 &&
    coverageStatus.some(
      status =>
        status !== healthInsuranceOrCoverageStatusEnumSchema.enum.NOT_COVERED,
    );

  const handleSectionCheckboxChange = (checked: boolean) => {
    if (!checked) {
      form.setValue(`${member.id}.healthInsuranceOrCoverageStatus`, []);
      form.setValue(`${member.id}.healthInsuranceOrCoverageOptionsOther`, "");
      form.setValue(`${member.id}.medicarePlanType`, null);
    }
  };

  const handleCoverageOptionCheckedChange = (
    checked: boolean,
    value?: string,
  ) => {
    if (
      !checked &&
      value === healthInsuranceOrCoverageStatusEnumSchema.enum.MEDICARE
    ) {
      form.setValue(`${member.id}.medicarePlanType`, null);
    }
  };

  return (
    <VnCard className="gap-4 rounded-md p-4">
      <VnAccordionItem value={member.id}>
        <VnAccordionTrigger
          className="flex w-full flex-row gap-1.5 py-0"
          showChevronIcon={false}>
          <VnBox className="flex min-w-0 flex-1 flex-row gap-x-2.5">
            <BaseCheckboxInput
              checked={Boolean(isSectionSelected)}
              onCheckedChange={handleSectionCheckboxChange}
            />
            <VnCardTitle variant="lgMedium" className="shrink">
              {formatHouseholdMemberName(member)}
            </VnCardTitle>
          </VnBox>
        </VnAccordionTrigger>
        <VnAccordionContent className="pb-0">
          <VnCardContent className="bg-background mt-4 gap-2.5 rounded-md p-4">
            <VnText
              variant="lgMedium"
              i18nKey="native:applicationCoverageStatusScreen.whatType"
            />
            <VnBox>
              <VnCheckboxInput
                control={form.control}
                name={`${member.id}.healthInsuranceOrCoverageStatus`}
                hasMultipleOptions
                isRequired={false}
                options={coverageOptions}
                inputClassName="bg-card"
                groupClassName="native:gap-4 web:gap-4"
                onCheckedChange={handleCoverageOptionCheckedChange}
                renderCheckedOptionContent={value =>
                  value ===
                    healthInsuranceOrCoverageStatusEnumSchema.enum.MEDICARE && (
                    <VnRadioGroupInput
                      control={form.control}
                      name={`${member.id}.medicarePlanType`}
                      options={medicarePlanTypeOptions}
                      className="mt-4"
                      radioGroupClassName="gap-4 bg-card p-4 rounded-md"
                      radioItemClassName="bg-background"
                    />
                  )
                }
              />

              {shouldShowOtherField && (
                <VnTextInput
                  control={form.control}
                  name={`${member.id}.healthInsuranceOrCoverageOptionsOther`}
                  isRequired={false}
                  placeholderI18nKey="native:applicationCoverageStatusScreen.pleaseSpecify"
                  className="ml-4 mt-2"
                />
              )}
            </VnBox>
          </VnCardContent>
        </VnAccordionContent>
      </VnAccordionItem>
    </VnCard>
  );
};

export const ApplicationCoverageStatusScreen = () => {
  const { t } = useTranslator();
  const router = useRouter();
  const setApplicationCurrentStep = useSetAtom(applicationCurrentStepAtom);
  const applicationTotalSteps = useAtomValue(applicationTotalStepsAtom);
  const { mutate: updateCurrentPatient } = useUpdateCurrentPatientMutation();

  const { data: householdMembers } = useListHouseholdMembersQuery();

  // Build default values from existing household member data
  const defaultValues = useMemo(() => {
    if (!householdMembers) return {};

    return householdMembers.reduce<HealthInsuranceOrCoverageStatusFormType>(
      (acc, member) => {
        acc[member.id] = {
          healthInsuranceOrCoverageStatus:
            member.healthInsuranceOrCoverageStatus ?? [],
          healthInsuranceOrCoverageOptionsOther:
            member.healthInsuranceOrCoverageOptionsOther ?? "",
          medicarePlanType: member.medicarePlanType ?? null,
        };
        return acc;
      },
      {},
    );
  }, [householdMembers]);

  const form = useForm<HealthInsuranceOrCoverageStatusFormType>({
    resolver: standardSchemaResolver(healthInsuranceOrCoverageStatusFormSchema),
    defaultValues,
  });

  // Reset form when default values change (e.g., after data loads)
  useEffect(() => {
    if (Object.keys(defaultValues).length > 0) {
      form.reset(defaultValues);
    }
  }, [defaultValues, form]);

  const { mutateAsync: updateHouseholdMember } =
    useUpdateHouseholdMemberMutation();

  // Track the patient's in-session Yes/No choice; fall back to the answer
  // derived from saved data.
  const [userAnswer, setUserAnswer] = useState<"yes" | "no" | null>(null);
  const derivedAnswer = useMemo(
    () => deriveCoverageStatusAnswer(householdMembers ?? []),
    [householdMembers],
  );
  const answer = userAnswer ?? derivedAnswer;
  const isSomeoneCovered = answer === "yes";

  const [isSaving, setIsSaving] = useState(false);
  const [openAccordions, setOpenAccordions] = useState<string[]>([]);

  const handleValueChange = (value: string[]) => {
    setOpenAccordions(value);
  };

  // Watch all form values to determine if next should be enabled
  const formValues = useWatch({ control: form.control });

  const isNextDisabled = useMemo(() => {
    if (!householdMembers || isSaving) return true;

    // "No" is already saved as not-covered for everyone; allow proceeding.
    if (answer === "no") return false;

    // Only the "Yes" answer requires a per-member coverage selection.
    if (answer !== "yes") return true;

    // Check if at least one member has a valid coverage status selection
    const hasAtLeastOneMemberWithValidCoverage = householdMembers.some(
      member => {
        const memberData = formValues[member.id];
        const hasSelection =
          memberData?.healthInsuranceOrCoverageStatus &&
          memberData.healthInsuranceOrCoverageStatus.length > 0 &&
          memberData.healthInsuranceOrCoverageStatus.some(
            status =>
              status !==
              healthInsuranceOrCoverageStatusEnumSchema.enum.NOT_COVERED,
          );

        if (!hasSelection) return false;

        // If "OTHER" is selected, check that the "other" field has a value
        const hasOther = memberData.healthInsuranceOrCoverageStatus?.includes(
          healthInsuranceOrCoverageStatusEnumSchema.enum.OTHER,
        );
        if (hasOther) {
          return (
            memberData.healthInsuranceOrCoverageOptionsOther &&
            memberData.healthInsuranceOrCoverageOptionsOther.trim().length > 0
          );
        }

        return true;
      },
    );

    return !hasAtLeastOneMemberWithValidCoverage;
  }, [householdMembers, formValues, answer, isSaving]);

  const navigateToNextStep = () => {
    updateCurrentPatient(
      {
        patientData: {
          applicationLastStep: "COVERAGE_STATUS",
        },
      },
      {
        onSuccess: () => {
          setApplicationCurrentStep(step =>
            Math.min(step + 1, applicationTotalSteps),
          );

          setOpenAccordions([]);

          router.navigate({
            pathname: "/(authed)/(modal)/application/date-of-birth",
          });
        },
      },
    );
  };

  const processUpdatePromises = async (
    updatePromises: ReturnType<typeof updateHouseholdMember>[],
  ) => {
    const results = await Promise.allSettled(updatePromises);

    const failedCount = results.filter(
      result => result.status === "rejected",
    ).length;
    const hasAllFailed = failedCount === results.length;

    if (hasAllFailed) {
      toast.error(
        t("native:eligibilitySpecialConditionsScreen.errors.allUpdatesFailed"),
      );
      return false;
    }

    if (failedCount > 0) {
      toast.error(t("native:someHouseholdMembersNotUpdated"));
    }

    navigateToNextStep();
    return true;
  };

  const handleSaveAndNext = async (
    data: HealthInsuranceOrCoverageStatusFormType,
  ) => {
    if (!householdMembers) return;

    setIsSaving(true);

    // Iterate over form data and update each household member
    const updatePromises = Object.entries(data).map(
      ([memberId, memberData]) => {
        return updateHouseholdMember({
          id: memberId,
          householdMemberData: {
            healthInsuranceOrCoverageStatus:
              memberData.healthInsuranceOrCoverageStatus,
            healthInsuranceOrCoverageOptionsOther:
              memberData.healthInsuranceOrCoverageOptionsOther,
            medicarePlanType: memberData.medicarePlanType,
          },
        });
      },
    );

    try {
      await processUpdatePromises(updatePromises);
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = () => {
    if (answer === "yes") {
      // Submit the form which triggers handleSaveAndNext
      void form.handleSubmit(handleSaveAndNext)();
    } else if (answer === "no") {
      // Already saved as not-covered (this session or previously); just advance.
      navigateToNextStep();
    }
  };

  const onNoPress = async () => {
    if (!householdMembers) {
      return;
    }

    const previousAnswer = userAnswer;
    setUserAnswer("no");
    setIsSaving(true);

    const updatePromises = householdMembers.map(member => {
      return updateHouseholdMember({
        id: member.id,
        householdMemberData: {
          healthInsuranceOrCoverageStatus: [
            healthInsuranceOrCoverageStatusEnumSchema.enum.NOT_COVERED,
          ],
          healthInsuranceOrCoverageOptionsOther: null,
          medicarePlanType: null,
        },
      });
    });

    try {
      const isSaved = await processUpdatePromises(updatePromises);
      if (!isSaved) {
        setUserAnswer(previousAnswer);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const onYesPress = () => {
    setUserAnswer("yes");
  };

  const householdMembersNames = getAllHouseholdMembersNames(
    householdMembers,
    "firstNameOnly",
  );

  return (
    <ApplicationScreenLayout
      onNextButtonPress={handleNext}
      isNextButtonDisabled={isNextDisabled}
      isNextButtonLoading={isSaving}
      titleI18nKey="native:applicationCoverageStatusScreen.title"
      descriptionI18nKey="native:applicationCoverageStatusScreen.description"
      descriptionI18nOptions={{
        householdMembersNames: householdMembersNames.join(", "),
      }}>
      <VnBox className="gap-3">
        <ApplicationYesNoButton
          onPress={onYesPress}
          titleI18nKey="common:yes"
          descriptionI18nKey="native:applicationCoverageStatusScreen.yesDescription"
          isSelected={answer === "yes"}
        />

        <ApplicationYesNoButton
          onPress={() => {
            void onNoPress();
          }}
          isLoading={isSaving && answer === "no"}
          titleI18nKey="common:no"
          descriptionI18nKey="native:applicationCoverageStatusScreen.noDescription"
          isSelected={answer === "no"}
        />
      </VnBox>

      {isSomeoneCovered && (
        <VnForm form={form} isInputOnlyForm>
          <VnText
            variant="lg"
            i18nKey="native:applicationCoverageStatusScreen.who"
            className="text-muted-foreground"
          />
          <VnAccordion
            type="multiple"
            value={openAccordions}
            onValueChange={handleValueChange}
            className="flex w-full">
            <VnBox className="gap-4">
              {householdMembers?.map(member => (
                <MemberCoverageStatusForm member={member} key={member.id} />
              ))}
            </VnBox>
          </VnAccordion>
        </VnForm>
      )}
    </ApplicationScreenLayout>
  );
};
